/*
 * brd-2026-editorial.js
 *
 * Performance strategy:
 * - Editorial videos have no `src` in the initial HTML.
 * - Only the correct mobile OR desktop video is loaded.
 * - The source is attached only when the section approaches the viewport.
 * - The video pauses when it leaves the viewport.
 *
 * This prevents below-the-fold editorial video from contributing large
 * media downloads to the initial page load.
 */

(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 749px)';
  var ROOT_MARGIN = '300px 0px';

  function isMobileViewport() {
    return (
      window.matchMedia &&
      window.matchMedia(MOBILE_QUERY).matches
    );
  }

  function getCorrectVideo(section) {
    if (!section) return null;

    var variant = isMobileViewport() ? 'mobile' : 'desktop';

    return section.querySelector(
      '.brd-editorial__video[data-brd-video-variant="' +
        variant +
        '"]'
    );
  }

  function ensureVideoLoaded(video) {
    if (!video) return false;

    if (video.dataset.brdVideoLoaded === '1') {
      return true;
    }

    var src = video.dataset.src;
    if (!src) return false;

    video.src = src;
    video.load();
    video.dataset.brdVideoLoaded = '1';

    return true;
  }

  function playVideo(video) {
    if (!ensureVideoLoaded(video)) return;

    var promise = video.play();

    if (promise && typeof promise.catch === 'function') {
      promise.catch(function () {
        // Autoplay can be blocked; poster remains visible.
      });
    }
  }

  function pauseAll(section) {
    section
      .querySelectorAll('.brd-editorial__video')
      .forEach(function (video) {
        video.pause();
      });
  }

  function activateCorrectVideo(section) {
    pauseAll(section);

    var video = getCorrectVideo(section);
    if (!video) return;

    playVideo(video);
  }

  function initSection(section) {
    if (
      !section ||
      section.dataset.brdEditorialInit === '1'
    ) {
      return;
    }

    section.dataset.brdEditorialInit = '1';

    var observer = null;
    var viewportQuery = window.matchMedia
      ? window.matchMedia(MOBILE_QUERY)
      : null;

    /*
     * We observe the section, not both video elements.
     * Nothing is loaded before this callback fires.
     */
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              activateCorrectVideo(section);
            } else {
              pauseAll(section);
            }
          });
        },
        {
          rootMargin: ROOT_MARGIN,
          threshold: 0.01
        }
      );

      observer.observe(section);
    } else {
      /*
       * Old-browser fallback: load the correct video.
       * Modern browsers used by Shopify customers support IntersectionObserver.
       */
      activateCorrectVideo(section);
    }

    function handleViewportChange() {
      /*
       * Useful for desktop resizing and Shopify Theme Editor.
       * Do not load anything if the section is still far away; if it has
       * already been loaded, this simply switches playback to the correct
       * visible variant.
       */
      var rect = section.getBoundingClientRect();
      var nearViewport =
        rect.top < window.innerHeight + 300 &&
        rect.bottom > -300;

      pauseAll(section);

      if (nearViewport) {
        activateCorrectVideo(section);
      }
    }

    if (viewportQuery) {
      if (typeof viewportQuery.addEventListener === 'function') {
        viewportQuery.addEventListener(
          'change',
          handleViewportChange
        );
      } else if (typeof viewportQuery.addListener === 'function') {
        viewportQuery.addListener(
          handleViewportChange
        );
      }
    }

    function handleVisibilityChange() {
      if (document.hidden) {
        pauseAll(section);
        return;
      }

      var rect = section.getBoundingClientRect();
      var nearViewport =
        rect.top < window.innerHeight + 300 &&
        rect.bottom > -300;

      if (nearViewport) {
        activateCorrectVideo(section);
      }
    }

    document.addEventListener(
      'visibilitychange',
      handleVisibilityChange
    );

    section.brdEditorialCleanup = function () {
      if (observer) {
        observer.disconnect();
      }

      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange
      );

      if (viewportQuery) {
        if (typeof viewportQuery.removeEventListener === 'function') {
          viewportQuery.removeEventListener(
            'change',
            handleViewportChange
          );
        } else if (typeof viewportQuery.removeListener === 'function') {
          viewportQuery.removeListener(
            handleViewportChange
          );
        }
      }

      pauseAll(section);
    };
  }

  function bootAll() {
    document
      .querySelectorAll(
        '.brd-editorial[data-section-id]'
      )
      .forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      bootAll
    );
  } else {
    bootAll();
  }

  /*
   * Shopify Theme Editor support.
   */
  document.addEventListener(
    'shopify:section:load',
    function (event) {
      var section = event.target.querySelector(
        '.brd-editorial[data-section-id]'
      );

      if (section) {
        initSection(section);
      }
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    function (event) {
      var section = event.target.querySelector(
        '.brd-editorial[data-section-id]'
      );

      if (
        section &&
        typeof section.brdEditorialCleanup === 'function'
      ) {
        section.brdEditorialCleanup();
      }
    }
  );
})();
